import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material'
import { DialogWrapper } from '../../../display-elements-components/dialog-wrapper'
import { UserNotificationsContext } from '../../../../../context/user-notifications/user-notifications'
import { UserDetailsContext } from '../../../../../context/user-details/user-details'
import ReportActionService from '../../../../../shared/services/report-action/report-action.service'
import { ActionStatus, IReportAction } from '../../../../../shared/services/report-action/report-action.model'

// The "I can't provide this" route. A data owner who cannot get a number no longer has to
// stall silently: they flag the task as blocked or waiting on a third party, with their
// reason, and the admins see it immediately. Admins can additionally record that the action
// is not applicable this period, which takes it out of outstanding work altogether.
export const ActionStatusDialog: FunctionComponent<{
  open: boolean,
  reportAction: IReportAction,
  closeHandler: () => void,
  onSaved: () => void,
}> = ({
  open,
  reportAction,
  closeHandler,
  onSaved,
}) => {

    const userNotificationsContext = useContext(UserNotificationsContext)
    const userDetailsContext = useContext(UserDetailsContext)
    const reportActionService = new ReportActionService()
    const isAdmin = !!userDetailsContext.userDetails?.roles.includes('admin')

    const [status, setStatus] = useState<ActionStatus>('blocked')
    const [reason, setReason] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
      if (open) {
        setStatus((reportAction.status as ActionStatus) || 'blocked')
        setReason(reportAction.statusReason || '')
        setIsSubmitting(false)
      }
    }, [open, reportAction.id])

    const submitHandler = () => {
      if (!reason.trim()) return
      setIsSubmitting(true)
      reportActionService.updateActionStatus(reportAction.id, status, reason.trim())
        .then(() => {
          userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' })
          closeHandler()
          onSaved()
        })
        .catch(err => {
          const message = err?.response?.data?.message || 'There was an error, please try again'
          userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
        })
        .finally(() => setIsSubmitting(false))
    }

    return (
      <DialogWrapper
        open={open}
        onClose={closeHandler}
        title="I can't provide this"
        cancelHandler={closeHandler}
        submitHandler={submitHandler}
        isSubmitting={isSubmitting}
        disableSaveButton={!reason.trim()}
        submitButtonText='Save'
        maxWidth='sm'
      >
        <Stack spacing={1}>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Tell the team why this data is not available. The task stays visible with your reason, so nobody finds out at report time.
          </Typography>
          <RadioGroup value={status} onChange={(e) => setStatus(e.target.value as ActionStatus)}>
            <FormControlLabel
              value='blocked'
              control={<Radio />}
              label={<Stack>
                <Typography variant='body1'>I cannot get this data</Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>The task shows as blocked so an administrator can help.</Typography>
              </Stack>}
              sx={{ mb: 1, alignItems: 'flex-start' }}
            />
            <FormControlLabel
              value='waiting_external'
              control={<Radio />}
              label={<Stack>
                <Typography variant='body1'>Waiting on someone outside the organisation</Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>For example a supplier or landlord who has been asked for the figures.</Typography>
              </Stack>}
              sx={{ mb: 1, alignItems: 'flex-start' }}
            />
            {isAdmin && <FormControlLabel
              value='not_applicable'
              control={<Radio />}
              label={<Stack>
                <Typography variant='body1'>Not applicable this period</Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>Records a business fact. The task stops counting as outstanding work.</Typography>
              </Stack>}
              sx={{ mb: 1, alignItems: 'flex-start' }}
            />}
          </RadioGroup>
          <TextField
            label='Reason'
            required
            multiline
            minRows={2}
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder='e.g. The landlord bills us annually and the statement has not arrived yet.'
          />
        </Stack>
      </DialogWrapper>
    )
  }
